import { SearchInput } from '@/catalog-apps/components/component-library/shared/SearchInput'
import { cn } from '@/lib/utils'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import type { DocsSearchResult } from './docsTypes'
import { DocsSearchResultSnippet } from './DocsSearchResultSnippet'
import { useDocsSearch } from './useDocsSearch'

function DocsSearchResultItem({
  result,
  query,
  onSelect,
}: {
  result: DocsSearchResult
  query: string
  onSelect: (path: string) => void
}) {
  return (
    <li>
      <button
        type="button"
        className="docs-search-result"
        role="option"
        onClick={() => onSelect(result.path)}
      >
        <span className="docs-search-result-title">{result.title}</span>
        {result.snippet && <DocsSearchResultSnippet snippet={result.snippet} query={query} />}
      </button>
    </li>
  )
}

export function DocsSearch() {
  const navigate = useNavigate()
  const containerRef = useRef<HTMLDivElement>(null)
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const { results, isLoading, isError, hasIndex } = useDocsSearch(query)

  useEffect(() => {
    function handlePointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [])

  const handleSelect = useCallback(
    (path: string) => {
      navigate(path)
      setOpen(false)
      setQuery('')
    },
    [navigate],
  )

  const trimmedQuery = query.trim()
  const showDropdown = open && trimmedQuery.length > 0

  return (
    <div ref={containerRef} className={cn('docs-search relative w-80')}>
      <SearchInput
        value={query}
        onChange={(value) => {
          setQuery(value)
          setOpen(true)
        }}
        placeholder="Search documentation..."
        className="w-full"
        inputClassName="bg-background"
      />
      {showDropdown && (
        <div className="docs-search-dropdown absolute left-0 right-0 top-full z-30 mt-1">
          {isLoading && <p className="docs-search-message">Loading search index...</p>}
          {isError && <p className="docs-search-message">Search unavailable. Run pnpm build:docs.</p>}
          {!isLoading && !isError && hasIndex && results.length === 0 && (
            <p className="docs-search-message">No matching pages</p>
          )}
          {!isLoading && !isError && results.length > 0 && (
            <ul className="docs-search-results" role="listbox">
              {results.map((result) => (
                <DocsSearchResultItem
                  key={result.location}
                  result={result}
                  query={query}
                  onSelect={handleSelect}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
